import { FormInstance } from 'antd/lib/form'
import {
    AttributeDefinitionType,
    AttributesData,
    ConversationResponseDataType,
    NotificationPropsType,
    RatingsOnLinearConversationDataType,
    TaskAttribute,
    TaskMode,
    UpdatedAttributesData,
    UseCaseTypes,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import {
    AttributeValueType,
    UpdateFlatAttributeParams,
    UpdateWrappedAttributeParams,
} from 'Types/Attribute.types'
import { sendAnalyticsTrack } from 'Utils/analytics.utils'
import { createReactElement } from 'shared/utils/react.utils'
import { convertFlatToWrappedAttributes } from 'Utils/attributes.utils'
import { AttributesFormWrapper } from 'Components/tasks/Attributes/AttributesFormWrapper'

export class AttributeGroup extends BaseAttributeGroup {
    groupKey = AttributeDefinitionType.EXTRA_INFO
    attributeDefinitionType = AttributeDefinitionType.EXTRA_INFO as const
    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        this.build()
    }

    // TODO: Remove this after BE migration
    // generation
    private initAttributeGeneration = (
        attrName: string,
        val: NonNullable<AttributeValueType>,
        attributes: TaskAttribute[]
    ) => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationResponseDataType
        if (!data?.response.extra_info) {
            return
        }

        data.response.extra_info[attrName] = {
            data: {
                value: val,
                value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                    .value_type,
            },
        }
    }

    // TODO: Remove this after BE migration
    // ratings
    private initAttributeRatings = (
        attrName: string,
        val: NonNullable<AttributeValueType>,
        attributes: TaskAttribute[]
    ) => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        if (!data?.extra_info) {
            return
        }

        data.extra_info[attrName] = {
            data: {
                value: val,
                value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                    .value_type,
            },
        }
    }

    // TODO: Keep only this after BE migration
    // text annotation, custom, others
    private initAttribute(
        attrName: string,
        val: NonNullable<AttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationResponseDataType
        if (!data) {
            return
        }
        if (!data.response.extra_info) {
            data.response.extra_info = {}
        }

        data.response.extra_info[attrName] = {
            value: val,
            value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                .value_type,
            value_id: crypto.randomUUID(),
        }
    }

    // TODO: Remove this after BE migration
    // generation, ratings
    private updateWrappedAttribute = <T>({
        attrName,
        attrValue,
    }: UpdateWrappedAttributeParams<T>) => {
        const container =
            this.attributeContext.taskConfig.assignment.task.type ===
            UseCaseTypes.RLHF_RATING_RESPONSE_RATING
                ? this.getContainerRatings()
                : this.getContainer() ?? {}

        if (attrValue === undefined) {
            if (container[attrName]) {
                delete container[attrName]
            }
        } else {
            if (container[attrName]) {
                container[attrName].data.value = attrValue as NonNullable<AttributeValueType>
            } else {
                if (
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_RATING_USECASE
                ) {
                    this.initAttributeGeneration(
                        attrName,
                        attrValue as NonNullable<AttributeValueType>,
                        this.attributes
                    )
                } else if (
                    this.attributeContext.taskConfig.assignment.task.type ===
                    UseCaseTypes.RLHF_RATING_RESPONSE_RATING
                ) {
                    this.initAttributeRatings(
                        attrName,
                        attrValue as NonNullable<AttributeValueType>,
                        this.attributes
                    )
                }
            }
        }
    }

    // TODO: Keep only this after BE migration
    // text annotation, custom, others
    private updateFlatAttribute = <T>({ attrName, attrValue }: UpdateFlatAttributeParams<T>) => {
        const container = (this.getContainer() ?? {}) as unknown as UpdatedAttributesData

        if (attrValue === undefined) {
            if (container[attrName]) {
                delete container[attrName]
            }
        } else {
            if (container[attrName]) {
                container[attrName].value = attrValue as NonNullable<AttributeValueType>
            } else {
                this.initAttribute(
                    attrName,
                    attrValue as NonNullable<AttributeValueType>,
                    this.attributes
                )
            }
        }
    }

    // TODO: Remove this after BE migration
    // generation, text annotation, custom
    private getContainer = () => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationResponseDataType
        return data?.response?.extra_info as AttributesData
    }

    // TODO: Remove this after BE migration
    // ratings
    private getContainerRatings = () => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        return data?.extra_info as AttributesData
    }

    // TODO: Keep this after BE migration
    // generation, text annotation, custom
    private getValues = () => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationResponseDataType

        if (!data) {
            throw new Error('Data is not found')
        }

        // TODO: Conversations - Check where to use it
        const extraInfo = data.response.extra_info ?? {}

        if (Object.values(extraInfo as AttributesData)[0]?.data) {
            return extraInfo as AttributesData
        } else {
            return convertFlatToWrappedAttributes(extraInfo as UpdatedAttributesData)
        }
    }

    // TODO: Remove this after BE migration
    // ratings
    private getValuesRatings = () => {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        return data?.extra_info as AttributesData
    }

    build() {
        if (!this.attributes.length) {
            return
        }
        const config = this.attributeContext.taskConfig
        const props = {
            groupKey: this.groupKey,
            attributes: this.attributes,
            onUpdateAttribute: (attrName: string, val: AttributeValueType) => {
                if (
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_RATING_USECASE ||
                    this.attributeContext.taskConfig.assignment.task.type ===
                        UseCaseTypes.RLHF_RATING_RESPONSE_RATING
                ) {
                    this.updateWrappedAttribute({
                        attrName,
                        attrValue: val,
                    })
                } else {
                    this.updateFlatAttribute({
                        attrName,
                        attrValue: val,
                    })
                }
                this.attributeContext.saveTask({
                    onComplete: (notifications?: NotificationPropsType) =>
                        this.attributeContext.studio.exitTask(notifications),
                })
                this.render()
                this.emit(AttributeGroupEvent.Change)
                sendAnalyticsTrack('SupplFeedback_Submitted', {
                    ...this.attributeContext.getCommonAnalyticsObject(),
                    attribute_name: attrName,
                })
                this.updateAreMandatoryAttributesFilled(this.getAttributeValues())
            },
            attributesOnUI: this.attributeContext.attributesOnUI,
            getValues: this.getAttributeValues,
            setForm: (form: FormInstance) => {
                this.form = form
            },
        }
        this.element = createReactElement(AttributesFormWrapper, {
            ...props,
            isReadOnly: () =>
                config.mode !== TaskMode.Triager && this.attributeContext.task.isReadOnly,
            headerComponent:
                this.props?.header ?? this.attributeContext.task.createAttributeHeader(),
            getFeedback: this.attributeContext.task.contextHandler.getFeedback,
            mode: config.mode,
            isTrainingMode: this.attributeContext.task.contextHandler.isTrainingMode,
        })
    }

    private getAttributeValues = () => {
        if (
            this.attributeContext.taskConfig.assignment.task.type ===
            UseCaseTypes.RLHF_RATING_RESPONSE_RATING
        ) {
            return this.getValuesRatings()
        } else {
            return this.getValues()
        }
    }

    validate = async () => {
        const isAttributesFormValid =
            !this.attributes.length || (await this.form?.validateFields().catch(() => false))
        if (!isAttributesFormValid) {
            this.attributeContext.studio.inputManager.addViolation({
                id: 'attributes',
                type: 'error',
                message: 'Please fill all the required supplementary details',
            })
            this.attributeContext.studio.workbench.enqueueUiUpdate(ui => {
                ui.rightSidebar.changeActiveItemId('review')
            })
            return false
        }
        this.attributeContext.studio.inputManager.removeViolation('attributes', 'error')
        return true
    }

    removeAllAttributes() {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as ConversationResponseDataType
        if (!data) {
            return
        }
        data.response.extra_info = {}
    }
}
